import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Loader2, CheckCircle } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";

const Login = () => {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Auto-redirect if already logged in
  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = async () => {
    if (isLoading) return; // Prevent double clicks
    
    setIsLoading(true);
    try {
      await login();
      navigate('/dashboard', { replace: true });
    } catch (error) {
      // Error already handled in login function
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // If already logged in, show success message briefly before redirect
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
          <p className="text-foreground font-medium mt-4">You're signed in!</p>
          <p className="text-muted-foreground text-sm mt-1">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-background sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="font-heading text-lg sm:text-xl font-semibold tracking-tight" data-testid="logo">
              Transplacement
            </Link>
          </div>
        </div>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-56px)] sm:min-h-[calc(100vh-64px)]">
        {/* Left side - Image */}
        <div className="hidden lg:block relative">
          <img 
            src="https://images.unsplash.com/photo-1648747640271-e0270e2dac1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwc3R1ZGVudHMlMjBsYXB0b3B8ZW58MHx8fHwxNzcyNTYzMTY5fDA&ixlib=rb-4.1.0&q=85"
            alt="Students"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-transparent" />
          <div className="absolute bottom-12 left-12 max-w-md">
            <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-foreground">
              Make College Placements Transparent
            </h2>
            <p className="text-muted-foreground mt-4">
              Join thousands of students sharing real placement data to help others make informed decisions.
            </p>
          </div>
        </div>

        {/* Right side - Login form */}
        <div className="flex items-center justify-center p-6 sm:p-8 md:p-12">
          <div className="w-full max-w-md">
            <div className="text-center mb-6 sm:mb-8">
              <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold uppercase tracking-tight">
                Sign In
              </h1>
              <p className="text-muted-foreground mt-2 text-sm sm:text-base">
                Continue with your Google account to submit placements and track your contributions.
              </p>
            </div>

            <div className="space-y-4">
              {/* Google Login Button */}
              <button 
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-6 py-3 sm:py-4 border-2 border-border bg-background hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm sm:text-base"
                data-testid="google-login-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" width="24" height="24" className="flex-shrink-0">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span>Continue with Google</span>
                  </>
                )}
              </button>
            </div>

            <p className="text-center text-[10px] sm:text-xs text-muted-foreground mt-6 sm:mt-8">
              By continuing, you agree to our Terms of Service and Privacy Policy.
              <br />
              <span className="font-medium">Your submissions remain completely anonymous.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
